import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Reply, X } from 'lucide-react';
import { useRouter } from 'next/router';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { isShopifyBatchOwnerAction } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useAgentName } from '../../../../lib/use-agent-name';
import { latestProfileLinkCards } from '../../../../lib/profile-link-chat-card';
import { urlNamesConversation } from '../../../../lib/chat-nav';
import { usePreloadCreditMiner } from '../../../../lib/credit-miner-flag';
import { useMyAgents } from '../../../../lib/my-agents';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { someoneElseBubbleText } from '../../../../lib/share-offer-card';
import { useChoicesShownBi } from '../../../../lib/use-choices-shown-bi';
import { isFirstWorkspaceMilestone, latestShopifyBatchCards } from '../../../../lib/chat-cards';
import { clearsDraftOnConversationChange } from '../../../../lib/chat-draft-reset';
import { hasFired, markOnboardingFinished } from '../../../../lib/gtm-funnel';
import { useMarkOnboardingFinishedLive } from '../../../../lib/use-home-goal-picker';
import { usePageLoadBi } from '../../../../lib/use-page-load-bi';
import { useTranslation } from '../../../../lib/i18n';
import { useDictation } from '../../../../lib/use-dictation';
import { useVoiceNote } from '../../../../lib/use-voice-note';
import { combinedDividerLabel, computeDayDividers, dayDividerLabel, dividerKind } from '../../../../lib/chat-day-dividers';
import { computeUnreadDividerId, newestMessageStamp, readLastRead, writeLastRead } from '../../../../lib/unread-divider';
import { agentCardGroup, isAgentCardRunStart } from '../../../../lib/agent-card-groups';
import { questionAfterCards } from '../../../../lib/chat-question-order';
import { entryInsightsAboveOfferCard } from '../../../../lib/entry-insights-order';
import { packArrivalMessage } from '../../../../lib/pack-arrival-message';
import { buildParticipantRoster } from '../../../../lib/wa-group-message';
import { isBriefSummaryTurn, useMorningBrief } from '../../../../lib/brief';
import { resolveOpenScroll } from '../../../../lib/brief-open-scroll';
import type { ChatState, Message } from '../../../../lib/use-chat';
import { selectedOptionIds, supersededChoiceAskIds, type ChoiceOption } from '../../../../lib/message-choices';
import { pendingSurveyAsk, submittedSurveyAnswer, surveyAskOnARow, type SurveyAnswer } from '../../../../lib/message-survey';
import { reportSendDropped, restoredDraftText, shouldRestoreDraft, type SendOutcome } from '../../../../lib/send-drop';
import { SURVEY_BI, SURVEY_COMPONENT, SURVEY_STEP_TARGET } from '../../../../lib/survey-bi';
import { SurveyWidget } from '../MessageRow/SurveyWidget';
import type { ExternalChannel } from '../../../../lib/external-channel';
import { Button, ScrollArea, Typography } from '../../ui';
import { ChannelLockedBar } from '../../ChannelLockedBar';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { VoiceCallOverlay } from '../../../VoiceCallOverlay';
import { MessageRow } from '../MessageRow';
import { ChatTurnProgress, ConversationLoader } from '../loaders';
import { AttachmentChipRow, ContactChipRow } from '../attachments';
import { ContactPicker } from '../ContactPicker';
import { PlacePicker } from '../PlacePicker';
import { TimePicker } from '../TimePicker';
import type { PlaceSummary } from '../../../../lib/use-place-search';
import { appendComposerLine, placeLine, shareTimeLine } from '../../../../lib/composer-inserts';
import { EmptyLandingHero } from '../EmptyLandingHero';
import { SuggestedTaskChips } from '../SuggestedTaskChips';
import { dropSuggestedTask, parkSuggestedTask } from '../../../../lib/suggested-task-ack';
import type { SuggestedTask } from '../../../../lib/suggested-tasks';
import { useContainerNarrow } from '../../../../lib/use-container-narrow';
import { MissingMovementHero } from '../MissingMovementHero';
import { withoutSupersededPlaceholders } from '../../../../lib/superseded-placeholders';
import { CHAT_HOME_BI, continuesAssistantRun, isVisibleMessage, isWaitingForAssistant, lastIndexOf, latestTurnOutputKey, latestTurnStatus } from '../message-utils';
import { useAttachments } from './use-attachments';
import { useContactReferences } from './use-contact-references';
import { useAgentMentions } from './use-agent-mentions';
import { contactReferences, appendContactTokens } from '../../../../lib/contact-references';
import { withMentionReference, type MentionAgent } from '../../../../lib/agent-mentions';
import { useVoiceBridge } from './use-voice-bridge';
import { usePublishVoiceCallSummary } from '../../../../lib/voice-call-presence-context';
import { useVoiceCallEnabled } from '../../../../lib/voice-call-flag';
import { useScroll } from './use-scroll';
import { useConnectHandoff } from '../../../../lib/use-connect-handoff';
import { usePackHandoff, usePackWalkthroughPending } from '../../../../lib/use-pack-handoff';
import { usePackArrivalReveal } from '../../../../lib/use-pack-arrival-reveal';
import { useAutosend } from './use-autosend';
import { Composer } from './Composer';
import { RecapBanner } from './RecapBanner';
import { ProfileThreadHeader } from '../../profile-links/ProfileThreadHeader';
import { useRecapVisible } from '../../../../lib/use-recap-visible';
import { CreditsChatInlineCard, CreditsChatStickyBar } from '../../credits-widgets/CreditsChatWidget';
import { useCreditsWidgetState } from '../../../../lib/use-credits-widget-state';
import { noteCreditsWidgetSimMessage } from '../../../../lib/credits-widget-sim';
import { meetingRoomTurnOnly, showsMeetingInProgress, withoutMeetingRoomExchange } from '../../../../lib/meeting-room-rows';
import {
  conversationDecisionsRefreshKey,
  isOpenDecision,
  meetingApprovalPollDelayMs,
  placeMeetingApprovals,
  threadHasMeetingApproval,
} from '../../../../lib/meeting-approval';
import { assembleProposalRows, threadHasProposalsCard, type ProposalRow } from '../../../../lib/proposals-card';
import { useConversationDecisions } from '../../../../lib/use-conversation-decisions';
import { failedTurnRetryTarget } from '../../../../lib/failed-turn';
import { targetSearchVerdict } from '../../../../lib/open-target-search';
import { MeetingApprovalCard } from '../MeetingApprovalCard';
import { DragOverlay, HorizonLine, ScrollToLatestButton, TopFade } from './chrome';
import { contentColumnStyle, formStyle, scrollAreaStyle, sectionStyle } from './styles';
import { SitePreviewLink } from '../../site-review/SitePreviewLink';
import { consumeComposerDraft, mergeDraft, observeReviewSend, reportChatTurnFinished, useComposerDraftRequest } from '../../../../lib/site-edit-store';

// At/below this pane width the empty chat switches to its tight form — the hero shrinks and the
// suggested-task chips move to a horizontal lane above the composer (Figma 17225:28520). Above it, the
// full hero + wrapped chips (Figma 17208:22003). ~560 is the point below which the centred wrapped rows
// stop reading well in the docked pane, which is why "not fully open but there's room" still wraps.
const EMPTY_CHAT_TIGHT_MAX_WIDTH = 560;

export function MessagesPanel({
  chat,
  readAcknowledgment,
  onLatestVisibilityChange,
  onInputAwakeChange,
  narrow,
  suppressEmptyLanding,
  missingMovement,
  variant,
  expanded,
  sideDocked,
  shouldAutosend,
  prefill,
  handoffOutreachId,
  handoffTaskId,
  handoffContext,
  handoffWorkspaceId,
  handoffAttachments,
  isNewConv,
  selectedId,
  prefillFromStore,
  consumePrefill,
  lockedChannel,
  lockedAgentPlatform,
  lockedIsWaGroup,
  recap,
  waitingForRecipient,
  surfaceVisible,
  meetingThread,
  briefThread,
}: {
  chat: ChatState;
  /** Whether the chat surface is actually OPEN — false while the floating chat rests (mounted, laid out,
   *  `visibility: hidden`). Only the survey card's impression reads it, and only to avoid counting an
   *  offer nobody could see behind a shut panel; absent = visible, the full-screen surface's case. */
  surfaceVisible?: boolean;
  /** The open conversation is a meeting's own thread (channel CALENDAR_MEETINGS). */
  meetingThread?: boolean;
  /**
   * The open conversation is a morning brief (row `source === 'BRIEF'`) — what both phones branch on.
   * TRI-STATE: `undefined` means the row has not resolved yet, which is NOT the same as "not a brief"
   * (see `classificationSettled` below). Collapsing the two is what lets a brief flash a link target.
   */
  briefThread?: boolean | undefined;
  readAcknowledgment?: { error: boolean; retry: () => void };
  onLatestVisibilityChange?: (visible: boolean) => void;
  onInputAwakeChange: (awake: boolean) => void;
  narrow: boolean;
  suppressEmptyLanding: boolean;
  missingMovement: boolean;
  variant: 'full' | 'floating';
  expanded: boolean;
  sideDocked: boolean;
  shouldAutosend: boolean;
  prefill: string | null;
  /** The inbound outreach a handoff prefill is REPLYING to — sent as `references.outreachId` so the
   *  agent relays it with the outreach tools instead of answering it. Null for every other handoff. */
  handoffOutreachId?: string | null;
  /** The task card a CTA opened this chat from; forwarded to the autosend as `references.taskId`. */
  handoffTaskId?: string | null;
  /** The handoff's hidden context line — sent as the stream endpoint's top-level `context`, never as text
   *  the user appears to have typed. See `ChatHandoffNew.context`. */
  handoffContext?: string | null;
  /** The Space a NEW thread is opened from — stamped on the conversation it creates. See `ChatHandoffNew.workspaceId`. */
  handoffWorkspaceId?: string | null;
  /** Attachments an imperative handoff already uploaded — a home-recorded voice note (empty text +
   *  the audio). See `PendingPrefill.attachments`. */
  handoffAttachments?: MessageAttachment[] | null;
  isNewConv: boolean;
  selectedId: string | null;
  prefillFromStore: boolean;
  consumePrefill: () => void;
  // Set when the open conversation is WhatsApp/SMS-owned (read-only here) —
  // the composer renders as ChannelLockedBar instead of the input.
  lockedChannel: ExternalChannel | null;
  // EXTERNAL_AGENT only — the platform name + link for the locked bar's CTA (see ChannelLockedBar).
  lockedAgentPlatform?: { name: string; url?: string } | null;
  lockedIsWaGroup: boolean;
  /** The conversation's rolling recap, when the server has written one. Rendered above the composer. */
  recap?: string;
  // The caller's recipient hasn't resolved yet — no identity to send under and no
  // onboarding decision, so hold the whole chat behind the transcript skeleton
  // rather than showing an empty/half-working composer.
  waitingForRecipient: boolean;
}) {
  usePreloadCreditMiner();
  // Composer/stream controls fire here (attach, send, stop, upload-failed); tag
  // them as the composer section under the "chat" chrome root.
  const { logPageClickBi } = useGenericBi('composer');
  const recapVisible = useRecapVisible(chat.conversationId ?? undefined);

  // Low/out-of-credits chat widget: an inline card at the top of the transcript, plus a condensed bar
  // that pins above the composer whenever the state applies and that card is scrolled off screen. Both
  // read `useCreditsWidgetState` (flag + live balance); a comfortable account shows neither. The inline
  // card reports its own on-screen visibility so the sticky takes over exactly when it leaves view.
  const creditsWidget = useCreditsWidgetState();
  const [creditsInlineVisible, setCreditsInlineVisible] = useState(false);
  const onCreditsInlineVisibility = useCallback((visible: boolean) => setCreditsInlineVisible(visible), []);
  // `showCreditsSticky` is derived below — it depends on the anchor's conversation binding.
  useChoicesShownBi(chat.messages, chat.conversationId, 'messages-panel');
  // The survey's impression is NOT here: an offer counts when the card is on screen, and the card lives
  // in the composer's slot below (`useSurveyShownBi`, wired inside `SurveyWidget`). Scanning the
  // transcript counted asks that never reached the reader — see use-survey-shown-bi.
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const {
    pendingChatAction,
    clearChatAction,
    pendingUploadFiles,
    clearUpload,
    pendingContacts,
    clearPendingContacts,
    minimizeChat,
    keepChatExpanded,
    briefPeekId,
  } = useActiveConversation();

  // Two independent axes:
  // - `inPanel`: rendered inside the floating overlay panel rather than anchored
  //   to the full chat pane. Drives layout plumbing (fill the host box, drop the
  //   sidebar offset / atmospheric surface / blur — the panel supplies its own).
  // - `compact`: the small-panel presentation (the overlay at rest). Its inverse
  //   — the full presentation — is the home OR the expanded overlay.
  const inPanel = variant === 'floating';
  const compact = !(variant === 'full' || expanded);

  // The empty chat has two Figma layouts and the pane's own width decides which (Figma 17208:22003,
  // "enough room", vs 17225:28520, the ~370px dock). At/below the threshold `chipsScrollRow` is true:
  // the hero draws its tight form and the suggested-task chips become a horizontal lane above the
  // composer; above it, the hero is full-size and the chips wrap under it. Measured on the pane rather
  // than the window, because the docked chat narrows the pane without touching `window.innerWidth`.
  const panelRef = useRef<HTMLElement>(null);
  const chipsScrollRow = useContainerNarrow(panelRef, EMPTY_CHAT_TIGHT_MAX_WIDTH);

  const pickSuggestedTask = useCallback(
    (task: SuggestedTask) => {
      setInputText(task.prompt);
      onInputAwakeChange(true);
      // Parked, not recorded: a tap only prefills, and the user may still clear it. `use-chat`'s `send`
      // flushes this if and when a message actually goes out.
      parkSuggestedTask(task.label);
    },
    [onInputAwakeChange],
  );

  // The "tail owes a reply" loader fallback (isWaitingForAssistant) is only meant
  // to survive a stream that DROPPED mid-session while the harness keeps running —
  // not a cold load of a stale turn, and not after the user hit Stop. Without
  // these guards the shimmer line shows forever on reload or after stopping.
  const hasStreamedRef = useRef(false);
  const [userStopped, setUserStopped] = useState(false);
  useEffect(() => {
    if (chat.streaming) {
      hasStreamedRef.current = true;
      setUserStopped(false);
    }
  }, [chat.streaming]);

  const [inputText, setInputText] = useState('');
  // Emptying the composer without sending is a decision NOT to send the chip that prefilled it, so the
  // parked label is dropped. Editing it is not — a reworded suggestion is still that suggestion, which
  // is the whole point of a prefill. Harmless after a send: `use-chat`'s `send` has already flushed and
  // cleared, so the clear that follows finds nothing parked.
  useEffect(() => {
    if (!inputText.trim()) dropSuggestedTask();
  }, [inputText]);
  // A handoff prefill with NO autosend is a draft: the words go in the composer, where the reader can
  // read them and decide, and the send stays theirs. `useAutosend` only ever sends — its whole body is
  // behind `shouldAutosend` — so without this the prompt is parsed, carried across the navigation and
  // then silently dropped.
  //
  // The pack accept's forward is what needs it: it strips the autosend precisely because nothing the
  // reader did authorises a send (`packForwardPrompt`), the prompt having been read off the landing URL
  // rather than issued with the pack. Same shape as a suggested-task chip — parked, not recorded.
  const seededPrefillRef = useRef(false);
  useEffect(() => {
    if (seededPrefillRef.current || shouldAutosend || !prefill) return;
    seededPrefillRef.current = true;
    // Never over-write something the reader is already typing.
    setInputText((prev) => (prev.trim() ? prev : prefill));
    onInputAwakeChange(true);
    // Drop the param so a reload does not re-seed a composer the reader deliberately cleared. History
    // API, like the collapse effect below, so FloatingChat's own URL writer is not clobbered.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('prefill');
      if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href);
    }
  }, [prefill, shouldAutosend, onInputAwakeChange]);
  // Pending quoted reply — set by a bubble's Reply action, rendered as a chip above the composer,
  // sent as `replyTo` and cleared on send/cancel/conversation switch.
  const [pendingReply, setPendingReply] = useState<Message['replyTo'] | null>(null);
  useEffect(() => {
    setPendingReply(null);
  }, [chat.conversationId]);

  // Dictation drops its transcript into the composer draft (appending to any text
  // already typed) so the user can review/edit before sending — it does NOT auto-
  // send. Wake the input so the composer reflects the new draft.
  const dictation = useDictation((text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setInputText((prev) => (prev.trim() ? `${prev.trim()} ${trimmed}` : trimmed));
    onInputAwakeChange(true);
  });


  const {
    fileInputRef,
    pendingAttachments,
    setPendingAttachments,
    dragActive,
    dragFileCount,
    openFilePicker,
    startUploads,
    onComposerPaste,
    removeAttachment,
    retryAttachment,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    uploadsInFlight,
    readyAttachments,
  } = useAttachments({ logPageClickBi, pendingUploadFiles, clearUpload });

  const { pickedContacts, addContact, removeContact, clearContacts } = useContactReferences({ pendingContacts, clearPendingContacts });

  // The draft belongs to the thread it was typed in — see `clearsDraftOnConversationChange` for which
  // transitions count. Clears the same things `send` does, so a staged upload or a contact chip cannot
  // follow the reader into another conversation either. The quoted reply above and the pending @-mention
  // (`use-agent-mentions`) already clear on this switch; this is the rest of the draft joining them.
  const draftConversationRef = useRef(chat.conversationId);
  useEffect(() => {
    const previous = draftConversationRef.current;
    draftConversationRef.current = chat.conversationId;
    if (!clearsDraftOnConversationChange(previous, chat.conversationId)) return;
    setInputText('');
    // Same revoke the send path does — a dropped preview whose blob URL is never released leaks it for
    // the life of the tab.
    pendingAttachments.forEach((p) => {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
    setPendingAttachments([]);
    clearContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the conversation switch is the trigger; the
    // draft it reads must be the one in hand at that moment, not a dep that re-fires the clear.
  }, [chat.conversationId]);

  // A composer DRAFT handed in from outside the chat — the site review's "Finish review" and the site hand-over's
  // ZIP offer (`lib/site-edit-store.ts`). Unlike a handoff prefill it is never sent: the words go in the composer of
  // the conversation it names (null = the new chat) and the owner sends them. Declared after the conversation-switch
  // clear above, so a draft that arrives with the switch is not wiped by it. A new chat's Space rides the owner's own
  // send (`draftWorkspaceRef`), the way a handoff stamps it.
  // A turn ending is when the agent's site publish (if any) has landed: the preview pane beside this chat reloads.
  const wasStreamingRef = useRef(chat.streaming);
  useEffect(() => {
    if (wasStreamingRef.current && !chat.streaming) reportChatTurnFinished(chat.conversationId);
    wasStreamingRef.current = chat.streaming;
  }, [chat.streaming, chat.conversationId]);
  // A site review Finish SENT here settles off this chat: its comments clear once the owner's message landed, and stay
  // (Finish works again) when the send was rolled back — the chat shows its own send error.
  useEffect(() => {
    observeReviewSend({
      conversationId: chat.conversationId,
      streaming: chat.streaming,
      userTexts: chat.messages.filter((m) => m.role === 'USER').map((m) => m.content ?? ''),
    });
  }, [chat.streaming, chat.messages, chat.conversationId]);
  const draftRequest = useComposerDraftRequest();
  const draftWorkspaceRef = useRef<string | null>(null);
  useEffect(() => {
    if (chat.conversationId) draftWorkspaceRef.current = null;
  }, [chat.conversationId]);
  useEffect(() => {
    if (!draftRequest || draftRequest.conversationId !== chat.conversationId) return;
    consumeComposerDraft(draftRequest.id);
    setInputText((prev) => mergeDraft(prev, draftRequest.text));
    onInputAwakeChange(true);
    if (draftRequest.conversationId === null) draftWorkspaceRef.current = draftRequest.workspaceId ?? null;
    if (draftRequest.attach) fileInputRef.current?.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new request, or the chat reaching its conversation
  }, [draftRequest, chat.conversationId]);

  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [placePickerOpen, setPlacePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  // Location + Share-a-time both just drop a readable line into the composer draft (no send-path
  // field, no references) — the scheduling assistant parses the place/time from the text, and the
  // maps URL is made tappable by the chat's LinkifiedText (mirrors inbound WhatsApp locations).
  const insertComposerLine = (line: string) => {
    setInputText((prev) => appendComposerLine(prev, line));
    onInputAwakeChange(true);
  };
  const onPickPlace = (place: PlaceSummary) => insertComposerLine(placeLine(place));
  const onPickTime = (date: Date) => insertComposerLine(shareTimeLine(date, i18n.language));

  const voice = useVoiceBridge(chat);
  const voiceCallEnabled = useVoiceCallEnabled();

  // Publish the live call outward so the app chrome can draw the island. The call
  // itself stays here — this panel owns the session, the conversation it writes
  // into, and the tool loop — and only a read-only summary plus the two
  // presentation controls travel. See lib/voice-call-presence-context.tsx.
  usePublishVoiceCallSummary({
    // The call surface owns the whole panel — so the app chrome drops its own ChatHeader/hamburger —
    // while connecting/live AND while the blocked-mic error surface is up (it keeps the voice header,
    // Figma "Mic- no access"). Other failures fall back to the chat header beside their Dismiss.
    active:
      voice.status === 'connecting' ||
      voice.status === 'live' ||
      (voice.status === 'error' && voice.errorDetail === 'mic-denied'),
    timing: voice.timing,
    muted: voice.muted,
    working: voice.working,
    minimize: voice.minimize,
    expand: voice.expand,
    // The island can hang up without expanding first, so the panel's own `end`
    // travels with the summary — one teardown path shared by both surfaces.
    end: voice.end,
  });

  // Whose surface is this — the CALL'S or the USER'S? Latched per call, and the only thing that
  // decides what closing the call does to the chat behind it (see the teardown effect below).
  // A call launched from the home composer opens the chat solely to host itself; a call started
  // from this composer happened on a screen the user had already navigated to.
  const chatOpenedForCallRef = useRef(false);

  // Starting a call must run straight off the click: primeAudioGesture() anchors
  // the browser's autoplay activation synchronously, so anything awaited before
  // it loses the gesture and the call connects mute.
  const startCall = voiceCallEnabled
    ? () => {
        logPageClickBi({ ...CHAT_HOME_BI, button_name: 'start_voice_call', location: 'composer' });
        // The user is already here. Hanging up must leave them here.
        chatOpenedForCallRef.current = false;
        voice.start();
      }
    : undefined;

  // Consume a voice/attach action requested from the home launcher. The launcher
  // can't run these itself (the chat + this infra is unmounted while it's a
  // bubble) — it stashes the intent and opens the chat, and we run it here on
  // mount, still inside the browser's user-activation window so the prompt opens.
  useEffect(() => {
    if (!pendingChatAction) return;
    if (pendingChatAction === 'voice') {
      // The chat is on screen BECAUSE of this call — it is the call's surface, not a destination.
      chatOpenedForCallRef.current = true;
      void voice.start();
    } else fileInputRef.current?.click();
    clearChatAction();
    // React only to a freshly-requested action; voice/clearChatAction are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChatAction]);

  // Two orders, deliberately: `arrived` is what landed, `visible` is what is drawn. Anything that means
  // "newest" reads `arrived` — the reorder can leave a settled ask last. See lib/chat-question-order.
  //
  // ⚠️ Memoized because `visible` is a DEPENDENCY of the credits-anchor effect below. Rebuilt inline it
  // was a new array every render, so that effect re-ran every render and scheduled two no-op updates —
  // enough pending work per commit to trip React's nested-update limit after ~50 keystrokes (#185).
  // A meeting's thread draws its write-up card, not the exchange the room had with the agent during the call.
  // …and a placeholder the same exchange already answered with a real file is not drawn (the finished PDF
  // under a "Rendering your image" game that never ends) — see lib/superseded-placeholders.
  const arrived = useMemo(
    () => withoutSupersededPlaceholders(withoutMeetingRoomExchange(latestShopifyBatchCards(chat.messages).filter(isVisibleMessage))),
    [chat.messages],
  );
  // A pack link's site insights are drawn above the offer card they followed, so the offer stays last
  // (lib/entry-insights-order).
  const visible = useMemo(() => questionAfterCards(entryInsightsAboveOfferCard(arrived)), [arrived]);
  // …and drawn as one message with the greeting above them and one toolbar, stamped with the click
  // (lib/pack-arrival-message).
  const arrivalMessage = useMemo(() => packArrivalMessage(visible), [visible]);
  // Latest card wins: every profile-link card draws the status of the LAST card for its link (the harness stamps one
  // after every write), so an earlier card follows a later pause or archive (lib/profile-link-chat-card).
  const profileLinkCards = useMemo(() => latestProfileLinkCards(chat.messages), [chat.messages]);
  // A re-ask closes the older card: an unanswered Choices card is live only on the NEWEST assistant row
  // with choices (lib/message-choices `supersededChoiceAskIds`, same rule as iOS/Android). Read off the
  // DRAWN list, so a row the panel drops can never close the card the reader is looking at.
  const supersededAsks = useMemo(() => supersededChoiceAskIds(visible), [visible]);
  // Rows that open a new local calendar day → the dated hairline above them. Computed on `visible`,
  // the list actually DRAWN (already `isVisibleMessage`-filtered and in render order), never
  // `chat.messages`: a divider anchored to a row the panel drops renders above the wrong message.
  const dayDividers = useMemo(() => computeDayDividers(visible), [visible]);
  const meetingInProgress = showsMeetingInProgress(!!meetingThread, chat.messages, arrived);
  // The one failed-turn row that offers "Try again", and what it re-sends (lib/failed-turn.ts). Read off
  // the FULL transcript — a USER row the chat hides is still something said after the failure.
  const failedTurnRetry = useMemo(() => failedTurnRetryTarget(chat.messages), [chat.messages]);

  // A guest's question from a live meeting, waiting on the owner: the card sits under the row that asks
  // it (lib/meeting-approval). Read on every chat open; closed approvals only where an approval row
  // exists, re-read as that thread grows so a settle made elsewhere reaches the card.
  const approvalThread = useMemo(() => threadHasMeetingApproval(arrived), [arrived]);
  // Same for the proposals card (lib/proposals-card): its rows show how each proposal ended, so a chat
  // holding one asks for closed proposals too. A proposal row the marker names but the read lacks is
  // simply not drawn — never an open-looking row from the marker alone.
  const proposalsThread = useMemo(() => threadHasProposalsCard(arrived), [arrived]);
  const spokenCount = useMemo(
    () => (approvalThread ? chat.messages.filter((m) => m.spokenAloud).length : 0),
    [approvalThread, chat.messages],
  );
  // Polled only while an approval on screen is still open — see `meetingApprovalPollDelayMs`.
  const [approvalPollMs, setApprovalPollMs] = useState<number | undefined>(undefined);
  const conversationDecisions = useConversationDecisions(chat.conversationId ?? undefined, {
    includeClosedMeeting: approvalThread,
    includeClosedProposals: proposalsThread,
    refreshKey: conversationDecisionsRefreshKey({
      approvalThread,
      proposalsThread,
      shownCount: arrived.length,
      spokenCount,
    }),
    pollMs: approvalPollMs,
  });
  const approvals = useMemo(() => placeMeetingApprovals(visible, conversationDecisions), [visible, conversationDecisions]);
  useEffect(() => {
    const open = [...approvals.anchored.values(), ...approvals.trailing].filter((row) => row.meetingApproval && isOpenDecision(row));
    setApprovalPollMs(meetingApprovalPollDelayMs(open, Date.now()));
  }, [approvals]);
  // A turn frame for the meeting room's own question answers the room, not the owner, so it shows no loader
  // unless an owner message is still owed its reply (lib/meeting-room-rows `meetingRoomTurnOnly`).
  const remoteTurnForOwner = useMemo(
    () => chat.remoteTurnActive && !meetingRoomTurnOnly(chat.messages),
    [chat.remoteTurnActive, chat.messages],
  );
  const proposalRowsByMessage = useMemo(() => {
    const byMessage = new Map<string, ProposalRow[]>();
    for (const m of visible) {
      if (m.role !== 'ASSISTANT' || !m.proposalsCard || !m.id) continue;
      const rows = assembleProposalRows(m.proposalsCard, conversationDecisions);
      if (rows.length > 0) byMessage.set(m.id, rows);
    }
    return byMessage;
  }, [visible, conversationDecisions]);

  // Anchor the inline low/out-of-credits card to the LATEST message at the moment the state first
  // became active, so it appears as a message in the conversation (and stays put as new turns arrive)
  // rather than pinned to the top. Cleared when the state clears; falls back to the bottom of the
  // transcript when there is no anchor message yet (fresh/empty thread).
  const [creditsAnchorId, setCreditsAnchorId] = useState<string | null>(null);
  // The conversation the inline card first appeared in. The inline card lives ONLY there; every OTHER
  // conversation shows just the sticky bar (a new chat opened while low/out is sticky-only, not a fresh
  // inline card). Recorded once, on first activation; cleared when the state clears.
  const [creditsAnchorConvId, setCreditsAnchorConvId] = useState<string | null>(null);
  useEffect(() => {
    if (creditsWidget.level === 'none') {
      setCreditsAnchorId(null);
      setCreditsAnchorConvId(null);
      return;
    }
    setCreditsAnchorId((prev) => {
      if (prev == null) {
        // Anchor to the newest message at the moment the state first became active. Prefer its canonical
        // server id (`finalId`) so a later refetch — which rebuilds rows keyed by the server id — still
        // matches; falls back to the in-session `id` while a fresh turn is still streaming.
        const last = visible[visible.length - 1];
        return last ? (last.finalId ?? last.id ?? null) : null;
      }
      // Promote a still-streaming anchor to its canonical server id once the turn settles, so the anchor
      // (and the card's place in the thread) survives the refetch that flips `id` from streamId → serverId.
      const anchored = visible.find((m) => m.id === prev || m.finalId === prev);
      if (anchored?.finalId && anchored.finalId !== prev) return anchored.finalId;
      return prev;
    });
    // Bind to the conversation on first activation only (keep it as we move between conversations).
    setCreditsAnchorConvId((prev) => (prev == null ? (chat.conversationId ?? null) : prev));
  }, [creditsWidget.level, visible, chat.conversationId]);
  // Are we in the conversation the card was anchored to? A null binding (first seen in a fresh/unsaved
  // thread) falls back to the current view, so the card still shows where it appeared.
  const inCreditsAnchorConv =
    creditsWidget.level !== 'none' && (creditsAnchorConvId == null || chat.conversationId === creditsAnchorConvId);
  // Match on EITHER id so the anchor holds across the streamId → serverId transition (see above) — but
  // only in the anchor conversation, so a second conversation never renders the inline card.
  const matchesCreditsAnchor = (m: Message): boolean =>
    inCreditsAnchorConv && !!creditsAnchorId && (m.id === creditsAnchorId || m.finalId === creditsAnchorId);
  const creditsAnchorInView = inCreditsAnchorConv && visible.some(matchesCreditsAnchor);
  // Sticky shows whenever the state applies AND the inline card isn't on screen — which includes every
  // conversation that is NOT the anchor one (where the inline card never renders, so it can't be "on
  // screen" and its stale visibility flag must not suppress the sticky).
  const showCreditsSticky = creditsWidget.level !== 'none' && (!inCreditsAnchorConv || !creditsInlineVisible);
  // The transcript scroll box is padded, not shortened, so the header overlays its top — a card scrolled
  // up behind the header is still inside the box. Feed the inline card the scroll container + this top
  // padding (matches `scrollAreaStyle`) so its observer treats "behind the header" as off-screen and the
  // sticky bar appears (the mobile-web fix).
  const creditsScrollTopInset = compact ? 14 : narrow ? (inPanel ? 20 : 64) : sideDocked ? 24 : 32;

  // What happens when the voice surface closes depends on WHOSE surface the chat is, and only then
  // on the call's outcome:
  //   - The chat was already open and the user started the call from its composer. The chat is
  //     where they navigated to; the call was something they did there. Closing the call returns
  //     them to it, always — CLOSE, whatever the call produced. This is what both phones do (a call
  //     is a screen pushed over the conversation, and popping it reveals the conversation again),
  //     and web was the only surface that did something else.
  //   - The chat was opened BY the home launcher's Speak, purely to host the call
  //     (`chatOpenedForCallRef`). Then:
  //       · produced at least one spoken turn → a real conversation exists, so leave its transcript
  //         on screen (CLOSE).
  //       · produced nothing → there is nothing to read and nothing the user asked to see, so
  //         DISMISS back to home. Keyed on the outcome flag rather than `visible.length`, so a
  //         productive call whose turns are still persisting (async reload) is never mistaken for
  //         empty.
  //   - Minimizing to the island keeps `status === 'live'`, so that path never reaches here — only a
  //     real teardown (idle/ended) does.
  //
  // ⚠️ THE BUG THIS REPLACES. The rule used to be "`producedTurns` false → dismiss", full stop, on
  // the reasoning that a call with no turns leaves nothing to read. But `producedTurns` only flips
  // on a final USER transcript from the speech model, while the surface says "Listening. Hearing
  // you." off the LOCAL mic level — so a caller who spoke, saw the app say it heard her, and hung
  // up a second later was ejected from the chat screen she had opened herself, all the way back to
  // home. Reported from mobile web ("I talk in voice mode, press X, and it throws me to home").
  // Whether a model produced a transcript is not a reason to take a screen away from someone.
  const prevVoiceStatusForReturnRef = useRef(voice.status);
  useEffect(() => {
    const prev = prevVoiceStatusForReturnRef.current;
    prevVoiceStatusForReturnRef.current = voice.status;
    const wasShowing = prev === 'connecting' || prev === 'live' || prev === 'error';
    const nowDone = voice.status === 'idle' || voice.status === 'ended';
    if (!wasShowing || !nowDone) return;
    const dismiss = chatOpenedForCallRef.current && !voice.producedTurns;
    // Per call: the next one re-latches at its own start, and nothing should inherit this one's.
    chatOpenedForCallRef.current = false;
    if (dismiss) minimizeChat();
    // Act only on the voice-status transition; the other reads are captured at that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.status]);

  // ===== Unread divider =====
  // On conversation open (history loaded), anchor a "New messages" hairline
  // before the oldest non-user message newer than the locally-stored last-read
  // marker (`lastRead:<conversationId>`). The divider stays for this viewing
  // session; the marker advances to the newest message as the thread updates,
  // so the next open is clean. Purely client-local — no backend.
  const [unreadDividerId, setUnreadDividerId] = useState<string | null>(null);
  const dividerComputedForRef = useRef<string | null>(null);
  // Conversation switch clears the session divider (recomputed on the next open).
  // MUST be declared BEFORE the compute effect: on a switch to a CACHED thread,
  // use-chat lands conversationId + messages + loading=false in ONE commit, so
  // both effects fire together — declared after the compute, this clear would
  // wipe the divider it just placed (and the ref-guard would block a recompute).
  useEffect(() => {
    setUnreadDividerId(null);
  }, [chat.conversationId]);
  useEffect(() => {
    const id = chat.conversationId;
    if (chat.history?.enabled) return;
    if (!id || chat.loading || chat.messages.length === 0) return;
    if (dividerComputedForRef.current === id) return;
    dividerComputedForRef.current = id;
    setUnreadDividerId(computeUnreadDividerId(chat.messages.filter(isVisibleMessage), readLastRead(id)));
  }, [chat.conversationId, chat.loading, chat.messages, chat.history?.enabled]);
  // Advance the marker while the user is looking at the thread (only after the
  // divider position was computed, so the compute never races the advance).
  useEffect(() => {
    const id = chat.conversationId;
    if (chat.history?.enabled) return;
    if (!id || chat.loading || dividerComputedForRef.current !== id) return;
    const newest = newestMessageStamp(chat.messages);
    if (newest) writeLastRead(id, newest);
  }, [chat.conversationId, chat.loading, chat.messages, chat.history?.enabled]);
  // The funnel's "Finish On boarding" = the user's FIRST workspace exists, which the harness
  // announces as a synthetic assistant row carrying `milestoneCard.kind === 'workspace'`
  // (stamped by ManageWorkspace's create action). That row is PERSISTED, so it replays into
  // `messages` on every conversation load — `markOnboardingFinished` is latched in
  // localStorage precisely so this reports the milestone once per browser rather than once
  // per page view. See lib/gtm-funnel.ts.
  //
  // `chat.messages` is rebuilt on EVERY streamed delta, so this effect runs per token. The
  // ref is what keeps that cheap: once the answer is settled — already latched, or reported
  // just now — it short-circuits before touching localStorage or rescanning the thread.
  const finishReportedRef = useRef(false);
  useEffect(() => {
    if (finishReportedRef.current) return;
    // Wait for history. Without this, a send that beats the initial load reads an empty
    // thread, misses a workspace card that is already there, and mislabels the send as
    // the funnel's START for a user who finished onboarding long ago.
    if (chat.loading) return;
    if (hasFired('finish')) {
      finishReportedRef.current = true;
      return;
    }
    if (!chat.messages.some(isFirstWorkspaceMilestone)) return;
    finishReportedRef.current = true;
    markOnboardingFinished();
  }, [chat.loading, chat.messages]);

  // The same milestone, ARRIVING in this session, is when the phones flip `finishedOnboarding` and the
  // home leaves the goal picker (lib/home-goal-picker.ts). Only a row that was not in the thread when its
  // history SETTLED counts (a cached window paints before revalidation adds older rows): a replayed milestone
  // says nothing the recipient read does not already say.
  const markFinishedLive = useMarkOnboardingFinishedLive();
  const loadedIdsRef = useRef<{ conversationId: string | null; ids: Set<string | undefined> } | null>(null);
  const finishedLiveRef = useRef(false);
  useEffect(() => {
    if (finishedLiveRef.current || chat.loading || !chat.readSettled) return;
    if (loadedIdsRef.current?.conversationId !== chat.conversationId) {
      loadedIdsRef.current = { conversationId: chat.conversationId, ids: new Set(chat.messages.map((m) => m.id)) };
      return;
    }
    const loaded = loadedIdsRef.current.ids;
    if (!chat.messages.some((m) => !loaded.has(m.id) && isFirstWorkspaceMilestone(m))) return;
    finishedLiveRef.current = true;
    markFinishedLive();
  }, [chat.loading, chat.readSettled, chat.conversationId, chat.messages, markFinishedLive]);

  // Roster for per-turn agent attribution — a message with `agentId` (a sub-agent's
  // round-table turn) renders as that agent's group-chat row. Resolve name/title
  // once here off the shared my-agents fetch; color is always by id.
  const team = useMyAgents();
  const agentsById = useMemo(
    () => new Map(team.status === 'ready' ? team.team.agents.map((a) => [a.id, a] as const) : []),
    [team],
  );

  // @-mention an agent: `@` at a word boundary opens the roster panel above the
  // composer; the pick leaves a literal `@AgentName ` token in the draft and the
  // structured id rides the send as `references.mentionedAgentId` (one per turn).
  const mentionRoster = useMemo<MentionAgent[]>(
    () => (team.status === 'ready' ? team.team.agents.map(({ id, name }) => ({ id, name })) : []),
    [team],
  );
  const mentions = useAgentMentions({
    inputText,
    setInputText,
    conversationId: chat.conversationId,
    agents: mentionRoster,
  });
  const pickMentionAgent = (agent: MentionAgent) => {
    logPageClickBi(
      {
        ...CHAT_HOME_BI,
        button_name: 'agent_mentioned',
        state: agent.id,
        ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
      },
      { componentName: agent.id },
    );
    mentions.pick(agent);
  };

  // The newest USER turn — the message a send scrolls up to the top.
  const lastUserMsgId = useMemo(() => {
    for (let i = visible.length - 1; i >= 0; i--) if (visible[i].role === 'USER') return visible[i].id ?? null;
    return null;
  }, [visible]);

  // The newest row on screen. Only it can be the one currently being written, so it is the only row the
  // streaming caret is offered to — see `turnActive` at the MessageRow call below.
  const tailMessageId = arrived.length ? arrived[arrived.length - 1].id : undefined;

  // A turn is in flight while the reply is streaming or still owed (mirrors the
  // in-render loader gate). The send-spacer is only needed for that window.
  const turnInFlight =
    chat.streaming || remoteTurnForOwner || (hasStreamedRef.current && !userStopped && isWaitingForAssistant(chat.messages));

  const brief = useMorningBrief();
  // Which turn is the round-table's wrap-up. The server stamps `message.agentMeetingSummary` (the
  // parsed messages API), but that route proxies to the deployed pod in local dev, so we ALSO derive
  // it on the client from the brief fetch — `/api/brief` returns each row's RAW metadata and the same
  // id, so `isBriefSummaryTurn` resolves it even before the field ships. Only for the brief that IS
  // this open conversation, so ids can't collide across threads.
  const briefSummaryIds = useMemo(() => {
    if (!brief || brief.conversationId !== chat.conversationId) return undefined;
    const ids = new Set<string>();
    for (const m of brief.messages) if (isBriefSummaryTurn(m)) ids.add(m.id);
    return ids.size > 0 ? ids : undefined;
  }, [brief, chat.conversationId]);

  /** The newest meeting-summary row in this thread — the only one that may carry the open questions. */
  const lastSummaryId = useMemo(() => {
    for (let i = chat.messages.length - 1; i >= 0; i -= 1) {
      const m = chat.messages[i];
      if (m.agentMeetingSummary || (!!m.id && briefSummaryIds?.has(m.id))) return m.id;
    }
    return undefined;
  }, [chat.messages, briefSummaryIds]);

  // Is the OPEN conversation a morning brief? Four independent signals, because the authoritative one is
  // slow: `briefThread` is the conversation row's own `source === 'BRIEF'` (what both phones branch on)
  // but the row is fetched in parallel with the transcript; the home peek and today's brief id answer
  // instantly but only for TODAY's brief; and the loaded transcript carrying a wrap-up row
  // (`lastSummaryId`) answers for any brief, including an old one reached by link, as soon as its rows
  // are in hand — which is also the first moment anything could be scrolled to.
  const isBriefConversation =
    briefThread === true ||
    (!!brief && !!chat.conversationId && brief.conversationId === chat.conversationId) ||
    (!!briefPeekId && briefPeekId === chat.conversationId) ||
    lastSummaryId !== undefined;

  // …and do we yet know it is NOT one? `briefThread === false` is the row answering; `chat.readSettled`
  // is the backstop so a row fetch that FAILS cannot strand an ordinary chat with its deep link disarmed
  // forever. Until one of them lands, the open carries no target at all — see `resolveOpenScroll`.
  const briefClassificationSettled = briefThread !== undefined || chat.readSettled;

  // Where this open lands and whether that row flashes the highlight — the whole rule, including the
  // brief's "no target, land on the wrap-up card", is `resolveOpenScroll` (lib/brief-open-scroll.ts).
  // The summary row comes from the TRANSCRIPT, not from the `/api/brief` payload `briefSummaryScrollId`
  // used to read, so a brief opened from history — not just today's peeked one — lands the same way.
  //
  // Open-at-message (`?message=` from a Chats search hit or a notification card) reads the normal LATEST
  // window; the target only drives the scroll and the highlight. A target older than that page is paged
  // back to — at most OPEN_TARGET_PAGE_CAP pages per open — and one that cannot be reached is given up on,
  // settling at the bottom with no highlight. The decision is `targetSearchVerdict`; iOS/Android share it.
  // A brief's `openTarget` is null, so none of that machinery runs for one.
  const { openTarget, scrollToMessageId: openScrollId, highlightOnOpen } = resolveOpenScroll({
    isBrief: isBriefConversation,
    classificationSettled: briefClassificationSettled,
    targetMessageId: chat.history?.targetMessageId,
    summaryMessageId: lastSummaryId,
  });
  const openTargetKey = openTarget && chat.conversationId ? `${chat.conversationId}:${openTarget}` : null;
  const [targetPages, setTargetPages] = useState<{ key: string | null; pages: number }>({ key: null, pages: 0 });
  const pagesLoaded = targetPages.key === openTargetKey ? targetPages.pages : 0;
  // A new open-at-target starts its own count — reopening the same link later must not inherit a spent cap.
  useEffect(() => setTargetPages({ key: openTargetKey, pages: 0 }), [openTargetKey]);
  const targetVerdict = openTarget
    ? targetSearchVerdict({
        found: chat.messages.some((m) => m.id === openTarget),
        drawn: visible.some((m) => m.id === openTarget),
        hasOlder: !!chat.history?.olderCursor,
        // Our own turn streaming makes `loadOlder` a no-op, so it waits rather than spending the cap.
        loadingOlder: !!chat.history?.loadingOlder || chat.streaming,
        loaded: chat.readSettled,
        // Only once THIS selection's read has landed: the commit that switches conversation still carries
        // the previous one's error, and a v1 open whose first read failed waits on its Retry instead.
        failed: chat.readSettled && !!chat.error,
        pagesLoaded,
      })
    : null;
  const loadOlderPage = chat.history?.loadOlder;
  useEffect(() => {
    if (targetVerdict !== 'page' || !openTargetKey || !loadOlderPage) return;
    setTargetPages({ key: openTargetKey, pages: pagesLoaded + 1 });
    void loadOlderPage();
  }, [targetVerdict, openTargetKey, pagesLoaded, loadOlderPage]);

  // Arrived here from accepting a pack: busy until the announcement turn shows up. Same bridge as the
  // connect handoff below, for a wait that is longer and starts from an empty transcript — see
  // `lib/pack-handoff`.
  const packHandoffPending = usePackHandoff(chat.conversationId);
  // A pack link opens at the TOP of its arrival message and the message grows downward as it types, so
  // nothing above what the reader sees moves. Kept for this conversation after the handoff expires:
  // dropping it would send the open effect back to the tail.
  const arrivalAnchorRef = useRef<{ conversationId: string; messageId: string } | null>(null);
  if (arrivalAnchorRef.current && arrivalAnchorRef.current.conversationId !== chat.conversationId) arrivalAnchorRef.current = null;
  if (packHandoffPending && arrivalMessage && chat.conversationId && !arrivalAnchorRef.current) {
    arrivalAnchorRef.current = { conversationId: chat.conversationId, messageId: arrivalMessage.firstId };
  }
  const { scrollRef, contentRef, onScroll, freezeScroll, releasePin, onScrollKeyDown, scrollToLatest, scrollProgress, sendSpacer, beginSendScroll } = useScroll({
    conversationId: chat.conversationId,
    lastUserMsgId,
    turnInFlight,
    scrollToMessageId: openScrollId ?? arrivalAnchorRef.current?.messageId ?? null,
    highlightOnOpen,
    openTargetUnreachable: targetVerdict === 'give-up',
  });
  useEffect(() => {
    onLatestVisibilityChange?.(!chat.loading && scrollProgress === 0);
  }, [chat.loading, scrollProgress, onLatestVisibilityChange]);

  const briefAssistant = useAgentName();
  // A brief thread is a multi-agent round-table. Detected the way native does (`isBriefConversation`):
  // this conversation IS the brief (its id matches the brief fetch — native's `source == "BRIEF"`), OR
  // any loaded turn carries an `agentId`. The id signal matters because the round-table OPENS with a
  // Maestro turn that has no agentId — without it that first turn renders unattributed until an agent
  // turn is seen. Maestro turns (no agentId) get the assistant name + mark; agent turns get their own.
  const isBriefThread =
    (!!brief && !!chat.conversationId && brief.conversationId === chat.conversationId) ||
    (visible.length > 0 && visible.some((m) => !!m.agentId));

  // Who is the Maestro/facilitator vs a sub-agent. The brief's sub-agents (Viola, Echo, …) are HIRED
  // agents that resolve in the roster; the Maestro is the primary assistant and is NOT a hired agent.
  // The round-table's opening/closing framing turns are the Maestro's — and the backend attributes them
  // with an `agentId` that doesn't belong to any hired agent — so a brief turn whose `agentId` doesn't
  // resolve to a roster agent (or has none) is the Maestro. Gated on the roster being loaded so a
  // real sub-agent isn't briefly mistaken for the Maestro before `my-agents` lands.
  const rosterReady = team.status === 'ready';
  const isMaestroTurn = (m: Message): boolean =>
    isBriefThread && (!m.agentId || (rosterReady && !agentsById.has(m.agentId)));

  // Distinct sub-agents who spoke, in first-spoke order — the meeting summary card's avatar cluster +
  // "N agents met" count (mirrors native `briefParticipantAgentIds`). Excludes the Maestro's own turns.
  const briefParticipantIds = useMemo(() => {
    if (!isBriefThread) return [];
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const m of visible) {
      const id = m.agentId;
      if (!id || (rosterReady && !agentsById.has(id))) continue;
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
    return ids;
  }, [isBriefThread, visible, rosterReady, agentsById]);

  // WhatsApp GROUP: build the `waId → displayName` roster from the attributed
  // turns themselves so `@<number>` mentions resolve to names. Only when the
  // open conversation is a group (avoids parsing every 1:1 thread).
  const groupRoster = useMemo(
    () => (lockedIsWaGroup ? buildParticipantRoster(visible.map((m) => m.content)) : undefined),
    [lockedIsWaGroup, visible],
  );

  // Empty landing: no conversation selected, no messages, no in-flight load.
  // ChatGPT/Claude-style centered input + 4U suggestions below — once a message
  // lands or one is being typed, fall back to the input-pinned chat layout.
  //
  // Suppress when the parent is about to autosend a prefill — chat.send fires in
  // a useEffect, so on first paint streaming is still false and the empty landing
  // would flash for one frame before the optimistic user bubble appears.
  const [packTurnSeen, setPackTurnSeen] = useState(false);
  useEffect(() => {
    if (packHandoffPending && !packTurnSeen && chat.remoteTurnActive) setPackTurnSeen(true);
  }, [packHandoffPending, packTurnSeen, chat.remoteTurnActive]);
  const pendingPackWalkthrough = usePackWalkthroughPending({
    handoffPending: packHandoffPending,
    turnSeen: packTurnSeen,
    turnActive: chat.streaming || remoteTurnForOwner,
    hasCard: visible.some((message) => Boolean(message.bandOfferCard || message.bandCard || message.bandCreated)),
    stopped: userStopped,
    failed: Boolean(chat.error),
    // A local stream with an owner message is their own send/steer. The no-user onboarding stream
    // only greets before dispatching the real pack turn, so it must keep this bridge alive.
    userTurnStarted: chat.streaming && chat.messages.some((message) => message.role === 'USER' && !message.fromMeetingRoom),
  });
  // Walkthrough text types in one row at a time. A ready team card bypasses that queue so a prepared
  // offer is usable immediately, even while its greeting is still typing.
  // A folded agent-card run draws only its first row, so the rest are not part of the sequence.
  const revealRows = useMemo(() => visible.filter((m, i) => !m.agentCard || isAgentCardRunStart(visible, i)), [visible]);
  const packReveal = usePackArrivalReveal(packHandoffPending, revealRows);

  // A genuine pack arrival already owes a walkthrough, even before the first row or server stamp.
  // Show its progress line from this first frame. Stop/error/completion end the bridge, idle prepared
  // chats already have their card, and the URL marker expires if nothing ever starts.
  const isEmptyLanding =
    router.isReady &&
    !suppressEmptyLanding &&
    !pendingPackWalkthrough &&
    chat.conversationId === null &&
    visible.length === 0 &&
    !chat.loading &&
    !chat.streaming;
  usePageLoadBi(
    { page_name: 'chat_home', page_version: 'v3_chat_home', state: 'empty' },
    { enabled: variant === 'full' && isEmptyLanding, trackBack: true },
  );

  // No second "home". The real home sits behind this floating chat, so when the
  // chat is expanded with nothing to show (a bare `?new=1`, or a call that ended
  // without a turn) collapse it back to reveal the home instead of rendering a
  // duplicate landing over it. `isEmptyLanding` already excludes the autosend
  // handoff; the extra guards keep it from firing while a call is launching/live.
  useEffect(() => {
    if (!isEmptyLanding || router.pathname !== '/') return;
    // A deep link to a thread is not an empty landing, even for the render before the chat adopts it. In a
    // tab with no stored thread (a profile link opened from WhatsApp) the overlay mounts with no active
    // conversation and nothing loading, and its child effect here runs BEFORE ChatSurface seeds the store
    // from `?conversation=` — so without this the chat collapsed to its bubble and the visitor landed on
    // Home instead of in the thread (`/?chat=bubble&conversation=…`).
    if (urlNamesConversation(router.query)) return;
    // A deliberately-opened Maestro chat (Talk to Maestro) stays expanded even
    // empty — it's the requested surface, not an incidental handoff to collapse.
    if (keepChatExpanded) return;
    if (pendingChatAction || voice.status === 'connecting' || voice.status === 'live') return;
    // A handoff with files or contacts mounts as an "empty" landing for a beat before they
    // register — collapsing then would eat them.
    if (pendingUploadFiles || pendingAttachments.length > 0 || pendingContacts || pickedContacts.length > 0) return;
    minimizeChat();
    // Drop the handoff params so a reload doesn't re-expand into this empty state.
    // Written via the History API (like FloatingChat's URL writer) so the two
    // don't clobber each other the way a Next router.replace would.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      ['new', 'prefill', 'autosend', 'chat'].forEach((p) => url.searchParams.delete(p));
      if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href);
    }
  }, [
    isEmptyLanding,
    router.pathname,
    keepChatExpanded,
    pendingChatAction,
    pendingUploadFiles,
    pendingAttachments,
    pendingContacts,
    pickedContacts,
    voice.status,
    minimizeChat,
  ]);

  // A pack just accepted: its announcement turn is seconds behind the accept and invisible until it
  // starts, so an autosend must wait until the assistant has spoken (or the handoff's grace expires).
  // "Has spoken" means the announcement's SERVER-SIDE turn was seen after the accept (a pending
  // prediction on the conversation → `remoteTurnActive`) — not any assistant row (an accept that fell
  // back to an existing conversation already has those), and not `streaming`, which the user's own
  // message during the wait would also set. If no such turn is ever seen, PACK_HANDOFF_GRACE_MS ends
  // the wait and the prompt sends anyway.
  const awaitingPackAnnouncement = packHandoffPending && !packTurnSeen;
  useAutosend({
    turnInFlight: chat.streaming || chat.remoteTurnActive || awaitingPackAnnouncement,
    shouldAutosend,
    prefill,
    handoffOutreachId,
    handoffTaskId,
    handoffContext,
    handoffWorkspaceId,
    handoffAttachments,
    isNewConv,
    selectedId,
    prefillFromStore,
    consumePrefill,
    chat,
    router,
    pendingUploadFiles,
    uploadsInFlight,
    readyAttachments,
    pendingAttachments,
    setPendingAttachments,
    pendingContacts,
    contactsForSend: pickedContacts,
    clearContactsForSend: clearContacts,
    lockedChannel,
    beginSendScroll,
    waitingForRecipient,
  });

  const hasSendable =
    inputText.trim().length > 0 || readyAttachments.length > 0 || pickedContacts.length > 0;
  // A send WHILE a turn is streaming STEERS it — the message is persisted via the push endpoint and
  // the open stream keeps responding (no interrupt, no new stream) — as long as there's a live
  // conversation to fold into. Read-only channels and in-flight uploads still gate it.
  const canSteer = chat.streaming && !!chat.conversationId && !lockedChannel && !uploadsInFlight && hasSendable;
  const canSend = (!lockedChannel && !chat.streaming && !uploadsInFlight && hasSendable) || canSteer;
  // Stop is the streaming affordance only while the composer is empty (it doubles as a "still
  // writing…" indicator). The moment the user types a message to steer with, the button becomes Send
  // and routes to the steer path — clearing the field brings Stop back.
  const showStop = chat.streaming && !canSteer;

  // A send that never reached the server hands its text back — into this composer, only while it is still
  // empty and still showing the conversation the text was typed in (see `lib/send-drop.ts`).
  const restoreDroppedDraft = (draft: { conversationId: string | null; text: string }, outcome: SendOutcome) => {
    if (!shouldRestoreDraft(outcome)) return;
    setInputText((current) =>
      restoredDraftText(draft, { conversationId: draftConversationRef.current, input: current }) ?? current);
  };

  const submitMessage = () => {
    if (!canSend) {
      // Enter in a composer that refuses the send keeps the text, but used to say nothing about why.
      if (inputText.trim()) {
        const gate = lockedChannel ? 'locked' : uploadsInFlight ? 'uploading' : chat.streaming ? 'streaming' : 'unknown';
        reportSendDropped('blocked', { conversationId: chat.conversationId, textLength: inputText.trim().length, detail: gate });
      }
      return;
    }
    logPageClickBi({
      ...CHAT_HOME_BI,
      button_name: 'send_message',
      ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
    });
    // The GTM activation funnel's send-driven milestones are NOT reported here: this is
    // only one of four ways a user message is sent (the home-launcher prefill autosend,
    // a voice note and a choice-chip answer never reach it). They ride useChat's
    // send/steer instead, beside the AppsFlyer call. See lib/gtm-funnel.ts.
    //
    // Rebuilt to the wire shape, so a local-only field can never ride out.
    const sendable: MessageAttachment[] = readyAttachments.map((p) => ({
      fileName: p.attachment!.fileName,
      mimeType: p.attachment!.mimeType,
      url: p.attachment!.url,
      ...(p.attachment!.thumbnailUrl ? { thumbnailUrl: p.attachment!.thumbnailUrl } : {}),
      ...(p.attachment!.size !== undefined ? { size: p.attachment!.size } : {}),
    }));
    const attach = sendable.length > 0 ? sendable : undefined;
    // Contacts attached via "+ Contact": build the `references.contacts` map (keyed by display
    // name) and inject each `[name]` token into the text so the sent bubble shows what was attached.
    const baseReferences = contactReferences(pickedContacts);
    const outgoingText = baseReferences ? appendContactTokens(inputText, pickedContacts) : inputText;
    // A picked @-mention rides as `references.mentionedAgentId` — only while its literal
    // `@AgentName` token is still in the outgoing text (deleting the token drops the id).
    const references = withMentionReference(baseReferences, mentions.pending, outgoingText);
    // Mid-stream: fold into the running turn (push, no new stream). The push endpoint carries no
    // quoted-reply or references, so a steer drops the pending quote/contacts/mention — the text
    // still reaches the turn.
    // The typed text, not `outgoingText` — restoring must not paste the contact tokens back in.
    const draft = { conversationId: chat.conversationId, text: inputText };
    const sending = canSteer
      ? chat.steer(outgoingText, attach)
      : chat.send(outgoingText, attach, references, pendingReply ?? undefined, undefined, undefined, undefined, chat.conversationId ? undefined : (draftWorkspaceRef.current ?? undefined));
    void sending.then((outcome) => restoreDroppedDraft(draft, outcome));
    // Dev-sim only (no-op without the "appear after N messages" mode armed): count this sent message so
    // the credits widget can be made to appear mid-conversation.
    noteCreditsWidgetSimMessage();
    setInputText('');
    setPendingReply(null);
    pendingAttachments.forEach((p) => {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
    setPendingAttachments([]);
    clearContacts();
    mentions.clear();
    // A new turn glides the just-sent bubble to the TOP so the reply streams in below it. A steer
    // does NOT — the reply is already streaming, and yanking the view would shove the live text
    // off-screen; the steer bubble just appends in place.
    if (!canSteer) beginSendScroll();
  };

  // ===== Voice notes =====
  // The mic's OTHER half, and what the phones do whenever the composer is empty: record → the
  // normal attachment upload → a send with EMPTY text carrying the audio attachment. The harness
  // recognises exactly that shape, transcribes at the send seam and persists
  // `<voice>{transcript}</voice>`, which every surface renders as a player with its transcript
  // under it. Nothing here is special-cased — it is `chat.send`/`chat.steer` as usual.
  const voiceNote = useVoiceNote({
    // Leaving the thread throws an in-progress take away.
    resetKey: chat.conversationId,
    onStart: () => logPageClickBi({ ...CHAT_HOME_BI, button_name: 'voice_note_start', location: 'composer' }),
    onDiscard: () => logPageClickBi({ ...CHAT_HOME_BI, button_name: 'voice_note_delete', location: 'composer' }),
    onUploadFailed: (message, mimeType) =>
      logPageClickBi({ page_name: 'chat', button_name: 'attachment_upload_failed', state: mimeType, action: message.slice(0, 200) }),
    onSend: (attachment) => {
      logPageClickBi({
        ...CHAT_HOME_BI,
        button_name: 'voice_note_sent',
        ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
      });
      // A note recorded while a turn is running folds into it via the steer path, exactly as a
      // typed message does (see submitMessage above).
      if (chat.streaming && chat.conversationId) {
        chat.steer('', [attachment]);
      } else {
        chat.send('', [attachment]);
        beginSendScroll();
      }
    },
  });

  // Build the quote reference from the on-screen message: our row id anchors it; preview is a
  // short whitespace-collapsed snippet; sender mirrors what the transcript shows for that row.
  const startReply = (m: Message) => {
    logPageClickBi({ page_name: 'chat_home', page_version: 'v3_chat_home', button_name: 'reply_to_message' });
    const raw = (m.content || '').replace(/\s+/g, ' ').trim();
    const isAssistantRow = m.role === 'ASSISTANT' || m.role === 'ASSISTANT_STEP';
    const sender = isAssistantRow
      ? (m.agentId ? agentsById.get(m.agentId)?.name : undefined) || briefAssistant.name
      : t('chat.reply.you');
    setPendingReply({ messageId: m.id, ...(raw && { preview: raw.slice(0, 140) }), ...(sender && { sender }) });
  };

  // Toggle a reaction (from a chip or the quick-react bar). Never gated on
  // lockedChannel — reactions are in-app annotations, not channel sends.
  const reactToMessage = (m: Message, emoji: string) => {
    logPageClickBi({
      ...CHAT_HOME_BI,
      button_name: 'react_message',
      ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
    });
    chat.toggleReaction(m, emoji);
  };

  // Tap a choice chip: send the option's label as a normal user turn carrying
  // the structured {askId, optionId} answer. The optimistic bubble stamps the
  // answer, so the chip reads selected at once; chips stay tappable after —
  // another tap is another turn — and send() no-ops while a turn streams.
  const selectChoice = (m: Message, option: ChoiceOption) => {
    // send() no-ops mid-stream; skip the scroll glide too so a tap during a
    // running turn doesn't yank the live text off-screen.
    if (!m.choices || chat.streaming) return;
    // The chosen text rides `additionalFields`: without it the event says a choice was made but
    // not WHICH, and the option ids are per-turn so they can't be read after the fact. Matches the
    // native apps' `choice` key. `conversation_id` already derives the conversation entity.
    logPageClickBi(
      {
        ...CHAT_HOME_BI,
        button_name: 'choice_selected',
        ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
      },
      { additionalFields: { choice: option.label } },
    );
    chat.send(option.label, undefined, undefined, undefined, { askId: m.choices.askId, optionId: option.id });
    beginSendScroll();
  };

  // "Send to someone else" on a share offer: the user typed the person, so send an ordinary user turn
  // carrying the offer's ask, the someone-else option and that person. Deliberately NOT routed through
  // `selectChoice`: that one requires the option to be among the reply's `choices`, and this option is
  // never there — as a chip it would reach older apps as a button with nothing behind it.
  //
  // The address rides the USER's own row, which is the send's whole authorization: the model cannot
  // write a user row, and the server has no model-callable path that takes an address.
  const shareToSomeoneElse = (m: Message, recipient: { email: string; name?: string }): boolean => {
    const card = m.shareOfferCard;
    if (!card?.someoneElse || chat.streaming) return false;
    // Same `button_name` and fields on iOS and Android — the three surfaces are one funnel.
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: 'share_someone_else_send', ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}) },
      { additionalFields: { kind: card.kind, chips: String(card.chips.length) } },
    );
    chat.send(someoneElseBubbleText(card.labels.someoneElse ?? 'Send to someone else', recipient), undefined, undefined, undefined, {
      askId: `share-offer:${card.offerId}`,
      optionId: card.someoneElse.optionId,
      recipient,
    });
    beginSendScroll();
    return true;
  };

  const shareSomeoneElseOpened = (m: Message) => {
    const card = m.shareOfferCard;
    if (!card) return;
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: 'share_someone_else', ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}) },
      { additionalFields: { kind: card.kind, chips: String(card.chips.length) } },
    );
  };

  // Tap a band on the offer card: send its NAME as an ordinary user turn, which is exactly what the user
  // would otherwise have to type. The band offer mints no choice chips — the agent asks "which band do
  // you want" in prose — so before this the two panels were a picture of a decision with no way to take
  // it. There is no `{askId, optionId}` to carry, for the same reason.
  //
  // Returns whether the pick was actually taken, because the card opens the band's connect walk on the
  // same tap and must not open it for a pick this refused: a reader would then be authorizing apps for a
  // band nobody chose, with nothing in the thread saying they chose it.
  const pickBand = (option: { id: string; name: string; agents?: string; connectors?: string }): boolean => {
    if (chat.streaming) return false;
    logPageClickBi(
      {
        ...CHAT_HOME_BI,
        button_name: 'band_offer_selected',
        ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
      },
      // The whole band they chose, not just its name: which team and which connectors converted is the
      // question the offer's own impression sets up, and a name alone cannot answer it.
      { additionalFields: { band: option.name, option: option.id, agents: option.agents, connectors: option.connectors } },
    );
    chat.send(option.name);
    beginSendScroll();
    return true;
  };

  // Submit an inline survey: send the composed answer text as a normal user turn carrying the
  // structured `surveyAnswer`. The optimistic bubble stamps the answer, so the card settles at once;
  // send() no-ops while a turn streams.
  const submitSurvey = (answer: SurveyAnswer, text: string) => {
    // The card can now be on screen while the turn is still streaming (the harness hands the ask over
    // as soon as the model asks it — see `earlySurvey`), and `chat.send` no-ops mid-stream. Holding the
    // answer and flushing it when the stream closes is the only honest option: a reader who filled the
    // card in and pressed Submit watched their answer go nowhere otherwise.
    if (chat.streaming) {
      queuedSurveyAnswer.current = { answer, text };
      return;
    }
    // Only the answered-question COUNT rides BI — the prompts and option labels are model-authored
    // from the caller's context and name their contacts/clients, so they stay out of the warehouse
    // (same rule as the choices-shown event). `conversation_id` derives the conversation entity.
    logPageClickBi(
      {
        ...CHAT_HOME_BI,
        button_name: SURVEY_BI.submitted,
        ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
      },
      {
        // The card is ONE component in the catalogue, and the click's `entityType` says which part of it
        // acted — here, the submit. See `report` in SurveyWidget, which shapes every other step the same.
        componentName: SURVEY_COMPONENT,
        // The ASK, not the conversation — `entity` is one id/type pair, and pairing the conversation's
        // id with `submit` would publish an `entities` row claiming the conversation IS a submit. See
        // `report` in SurveyWidget; `conversation_id` carries the thread, as it does on every step.
        entity: { id: answer.askId, type: SURVEY_STEP_TARGET[SURVEY_BI.submitted] },
        additionalFields: {
          step: SURVEY_BI.submitted,
          // `ask_id` joins this to every other step of the card's funnel (lib/survey-bi).
          ask_id: answer.askId,
          answered_questions: String(answer.answers.length),
          // The split between tapping and typing, counted off what was actually SENT rather than off
          // the draft: a question answered in the write-in row is one the suggestions did not fit, and
          // that is the measure of whether the model is asking well.
          picked_answers: String(answer.answers.filter((a) => a.optionIds.length > 0).length),
          written_answers: String(answer.answers.filter((a) => !!a.freeText).length),
        },
      },
    );
    chat.send(text, undefined, undefined, undefined, undefined, undefined, answer);
    beginSendScroll();
  };

  // The answer submitted against a card that was still mid-turn, waiting for the stream to close.
  const queuedSurveyAnswer = useRef<{ answer: SurveyAnswer; text: string } | undefined>(undefined);
  useEffect(() => {
    if (chat.streaming) return;
    const queued = queuedSurveyAnswer.current;
    if (!queued) return;
    queuedSurveyAnswer.current = undefined;
    // Only if the ask LANDED. The card can be answered while the turn is still working, and not every
    // early ask survives that turn — chips outrank a survey on one reply, and the harness drops an offer
    // that real work followed. Flushing regardless would send an answer to a question the conversation
    // decided not to ask, under an askId no row carries and nothing downstream can match. Dropping it is
    // the honest end: the card it was typed into is gone by now, for the same reason.
    if (!surveyAskOnARow(chat.messages, queued.answer.askId)) return;
    submitSurvey(queued.answer, queued.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.streaming]);

  // The survey the user is being asked RIGHT NOW — it stands in the COMPOSER's place rather than
  // inline in the transcript, so an ask that is still open cannot be scrolled away from, and the
  // input is not offered beside a question the agent is waiting on.
  //
  // Which ask is CURRENT is `pendingSurveyAsk`'s rule (lib/message-survey) — it stops at the first
  // USER turn, so an ask the conversation has already moved past cannot take the input hostage.
  //
  // Dismissal (X / Skip / a nothing-answered submit) is session state kept HERE, keyed by askId, so
  // giving the slot back to the input survives the row remounting — and, exactly as before, a
  // reopened chat offers a still-current unanswered ask again.
  const [dismissedSurveyIds, setDismissedSurveyIds] = useState<ReadonlySet<string>>(new Set());
  const surveyLocked = !!lockedChannel;
  // The SAME ask, one step before any row carries it: the harness hands a survey over the moment the
  // model asks it, rather than at the end of the turn that persists it (`chat.earlySurvey`). The row
  // takes over the instant it arrives with the same askId — `pendingSurveyAsk` wins here — so the card
  // does not blink, and the session state below keys on the askId either way, which is why an X during
  // the turn stays an X after it.
  const pendingSurvey = useMemo(() => {
    if (surveyLocked) return undefined;
    const onRow = pendingSurveyAsk(chat.messages, dismissedSurveyIds);
    if (onRow) return onRow;
    const early = chat.earlySurvey;
    if (!early || dismissedSurveyIds.has(early.askId)) return undefined;
    if (submittedSurveyAnswer(chat.messages, early.askId)) return undefined;
    return { message: undefined, survey: early };
  }, [chat.messages, chat.earlySurvey, dismissedSurveyIds, surveyLocked]);

  // The ask the reader dismissed with the X and could still take back: it is STILL the current ask —
  // nothing has been said past it — so the prompts left in the transcript are a way back into the card
  // rather than a record of something closed. Undefined the moment the reader says anything at all,
  // which is `pendingSurveyAsk`'s own rule (it stops at the first USER turn): a survey the conversation
  // moved past is not reopenable, and the transcript row must not look clickable when it is not.
  const reopenableSurveyAskId = useMemo(() => {
    if (surveyLocked) return undefined;
    const current = pendingSurveyAsk(chat.messages);
    return current && dismissedSurveyIds.has(current.survey.askId) ? current.survey.askId : undefined;
  }, [chat.messages, dismissedSurveyIds, surveyLocked]);
  const reopenSurvey = (askId: string) => {
    // The one step of the funnel that runs BACKWARDS, and the only evidence that a dismissal was "not
    // now" rather than "no": without it a reopened-and-answered card is indistinguishable from one that
    // was never closed, and the dismissal it followed reads as a loss.
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: SURVEY_BI.reopened, ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}) },
      {
        componentName: SURVEY_COMPONENT,
        entity: { id: askId, type: SURVEY_STEP_TARGET[SURVEY_BI.reopened] },
        additionalFields: { step: SURVEY_BI.reopened, ask_id: askId },
      },
    );
    setDismissedSurveyIds((prev) => {
      const next = new Set(prev);
      next.delete(askId);
      return next;
    });
  };

  // A connect completed in this session: busy until the conversation itself says so. The second arg is
  // whether a real streamed THINKING line is up — it retires the post-connect placeholder the instant
  // the genome's own thinking arrives, so the placeholder never outlives the real thing.
  const connectHandoff = useConnectHandoff(latestTurnOutputKey(chat.messages), Boolean(latestTurnStatus(chat.messages)));

  // "Get a new link" on an expired connector card: the card hands us its localized ask,
  // sent as a normal user turn through the same composer send path as a choice tap.
  // BI for the tap fires in the card itself. send() no-ops mid-stream (same guard).
  const requestConnectorLink = (_m: Message, text: string) => {
    if (chat.streaming) return;
    chat.send(text);
    beginSendScroll();
  };

  // "Try again" on a failed turn: re-send the message it was answering, as a normal user turn through the
  // same send path as a typed message. send() no-ops mid-stream; the new USER row retires the button.
  const retryFailedTurn = (text: string) => {
    if (chat.streaming) return;
    chat.send(text);
    beginSendScroll();
  };

  const onStop = () => {
    logPageClickBi({
      page_name: 'chat_home',
      page_version: 'v3_chat_home',
      button_name: 'stop_generation',
      ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
    });
    setUserStopped(true);
    void chat.stop();
  };

  const missingHero = missingMovement && !chat.loading && visible.length === 0;

  if (waitingForRecipient) {
    return (
      <section
        className={inPanel ? undefined : 'orion-chat-pane'}
        style={sectionStyle(inPanel, chat.conversationId !== null)}
      >
        <div style={scrollAreaStyle(compact, narrow, inPanel, sideDocked)}>
          {pendingPackWalkthrough ? (
            <ChatTurnProgress
              inFlight
              packPending
              connectPending={false}
              packThinking={t('packs.accept.loading', { maestroAgentName: briefAssistant.name })}
            />
          ) : <ConversationLoader floating={compact} />}
        </div>
      </section>
    );
  }

  return (
    <section
      ref={panelRef}
      // What a band's connect walk centres its sign-in popup on (`lib/connect-walk-popup.ts`).
      data-chat-panel=""
      // The full pane uses .orion-chat-pane (its CSS sets `left: 280px` for the
      // sidebar). The overlay fills its host box instead, so it must NOT carry
      // that class or the `left` offset would shove it sideways.
      className={inPanel ? undefined : 'orion-chat-pane'}
      // Read-only (WhatsApp/SMS-owned) thread: no uploads either — a dropped
      // file could never be sent, so the drag/drop affordance stays off.
      onDragEnter={lockedChannel ? undefined : onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={lockedChannel ? undefined : onDrop}
      style={sectionStyle(inPanel, chat.conversationId !== null)}
    >
      {/* Voice call surface — `contained` inside the Symphony overlay so it fills
          the chat and rides the resize with it instead of taking over the
          whole viewport. */}
      <VoiceCallOverlay voice={voice} contained={inPanel} />
      {/* A live call's own content block (orb + status, from VoiceCallOverlay above) takes this
          same flex slot instead — voice always wins over the transcript/empty-landing view, same
          priority as before, just without also hiding the composer underneath it. */}
      {!voice.timing.surfaceVisible && (
        <>
      <TopFade visible={narrow && !inPanel} />

      {missingHero && <MissingMovementHero narrow={narrow} />}
      {!isEmptyLanding && !missingHero && (
        <ScrollArea
          ref={scrollRef}
          onScroll={onScroll}
          onPointerDown={freezeScroll}
          onWheel={releasePin}
          onTouchMove={releasePin}
          onKeyDown={onScrollKeyDown}
          style={scrollAreaStyle(compact, narrow, inPanel, sideDocked)}
        >
          <div ref={contentRef} style={contentColumnStyle(compact)}>
            {chat.history?.olderCursor && (
              <Button variant="text" disabled={chat.history.loadingOlder || chat.streaming} onClick={() => {
                logPageClickBi({ page_name: 'chats', button_name: 'load_earlier_messages' });
                const element = scrollRef.current;
                const top = element?.scrollTop ?? 0;
                const height = element?.scrollHeight ?? 0;
                freezeScroll();
                void chat.history?.loadOlder(true).then(() => requestAnimationFrame(() => {
                  if (element) element.scrollTop = top + element.scrollHeight - height;
                }));
              }}>{t('chats.loadOlder')}</Button>
            )}
            {chat.history?.enabled && chat.error && (
              <div role="alert"><Typography variant="p2">{t('chat.loadFailed.message')}</Typography>
                <Button variant="text" onClick={() => { logPageClickBi({ page_name: 'chats', button_name: 'retry_history' }); void chat.reload(); }}>{t('chat.turnFailed.retry')}</Button></div>
            )}
            {readAcknowledgment?.error && (
              <div role="alert"><Typography variant="p2">{t('chats.readFailed')}</Typography>
                <Button variant="text" onClick={() => { logPageClickBi({ page_name: 'chats', button_name: 'retry_read' }); readAcknowledgment.retry(); }}>{t('common.retry')}</Button></div>
            )}
            {chat.loading && <ConversationLoader floating={compact} />}
            {/* A profile link's thread is headed by whose card it is and what they see (profile links §6.6). */}
            <ProfileThreadHeader conversationId={chat.conversationId} />
            {meetingInProgress && (
              <Typography variant="p2" color="secondary" align="center" data-testid="meeting-in-progress">
                {t('chat.meetingInProgress')}
              </Typography>
            )}
            {visible.map((m, i) => {
              // Consecutive agentCard messages (one per hire) fold into ONE
              // multi-agent orbit card rendered at the FIRST message of the run;
              // the rest render nothing. The group is keyed by the first
              // message's id, so a new hire appended to the run EXTENDS the same
              // mounted card instead of remounting it (see lib/agent-card-groups).
              if (m.agentCard && !isAgentCardRunStart(visible, i)) return null;
              // A pack walkthrough row waiting for the one before it to finish typing.
              if (m.id && packReveal.held.has(m.id)) return null;
              // The facilitator's framing turns carry an `agentId` that isn't a hired agent — treat
              // those (and any un-attributed turn) as the Maestro, not a sub-agent.
              const maestroTurn = isMaestroTurn(m);
              const row = (
                <MessageRow
                  key={m.id}
                  message={m}
                  shopifyBatchActionable={!!m.shopifyBatchCard && !visible.slice(i + 1).some(row => row.shopifyBatchCard ||
                    (row.role === 'user' && isShopifyBatchOwnerAction(row.content ?? '', m.shopifyBatchCard?.batchId ?? '')))}
                  // The thread this row lives in, for its attachments alone: a rendering image's game claims
                  // its credits against it. Unlike `conversationId` below, which is withheld from most rows on
                  // purpose so their cards cannot act, a claim is a read-only settlement of the row's own render.
                  threadConversationId={chat.conversationId ?? undefined}
                  agentCardRun={m.agentCard ? agentCardGroup(visible, i) : undefined}
                  // A burst of back-to-back replies from the same speaker (no user turn between
                  // them) gets a tighter gap after this row instead of full turn-taking spacing.
                  tightenGapAfter={continuesAssistantRun(m, visible[i + 1])}
                  agent={maestroTurn ? undefined : m.agentId ? agentsById.get(m.agentId) : undefined}
                  // In a brief group-chat EVERY speaker is labeled — the Maestro's turns carry the
                  // resolved assistant name + mark; sub-agent turns carry their own. Normal 1:1 chats
                  // keep Maestro turns unlabeled (bare prose).
                  maestroName={maestroTurn ? briefAssistant.name : undefined}
                  brief={isBriefThread}
                  isMaestro={maestroTurn}
                  briefParticipantIds={briefParticipantIds}
                  isSummaryTurn={m.agentMeetingSummary || (!!m.id && briefSummaryIds?.has(m.id))}
                  // ONLY the newest summary carries the open questions. The rows are found by
                  // CONVERSATION, so handing every summary card the id would paint the same questions
                  // under each one — a thread with three mornings in it would show three live Approve
                  // buttons for one decision, and answering under one would leave the others answerable
                  // until a second tap got refused as already-settled by something the reader did not do.
                  // …and for a meeting's write-up row, whose "Add as task" sends into THIS thread — a row
                  // without the id renders the card with every task action missing. It takes the same
                  // gate as every other interactive prop below: a channel-locked thread accepts no sends
                  // at all, so the actions must not be there to press.
                  conversationId={
                    m.id && m.id === lastSummaryId
                      ? (chat.conversationId ?? undefined)
                      : m.meetingNotesCard && !lockedChannel
                      ? (chat.conversationId ?? undefined)
                      : undefined
                  }
                  // A send is REFUSED while a turn streams (`send` returns early), and the autosend latch
                  // consumes the handoff anyway — so the card's task actions would look like they worked
                  // and send nothing. They disable instead of vanishing: a card that reflows every time
                  // the agent speaks is worse than a button that is visibly not available yet.
                  canSendToChat={!chat.streaming}
                  // `send` refuses only while this browser streams, so a turn the server started would take a chip's message as a race.
                  shopifyBriefingActionable={!chat.remoteTurnActive}
                  isWaGroup={lockedIsWaGroup}
                  assistantName={briefAssistant.short}
                  participantsByWaId={groupRoster}
                  onReply={lockedChannel ? undefined : startReply}
                  onToggleReaction={reactToMessage}
                  selectedChoiceIds={m.choices ? selectedOptionIds(chat.messages, m.choices.askId) : undefined}
                  // A choice answer IS a channel send, so it locks with the composer.
                  // A SUPERSEDED ask (an older unanswered card the agent has since re-asked) renders closed:
                  // no handler → the chips draw inert, with no selection.
                  onSelectChoice={lockedChannel || (m.choices && supersededAsks.has(m.choices.askId)) ? undefined : selectChoice}
                  // Picking a band IS a channel send, so it locks with the composer like a choice does.
                  onPickBand={lockedChannel ? undefined : pickBand}
                  onShareSomeoneElse={lockedChannel ? undefined : shareToSomeoneElse}
                  onShareSomeoneElseOpened={shareSomeoneElseOpened}
                  shareSomeoneElseDisabled={chat.streaming}
                  // The re-mint ask is a channel send too, so it locks the same way.
                  onRequestConnectorLink={lockedChannel ? undefined : requestConnectorLink}
                  // Kick off is a channel send too — the same path as the re-mint ask.
                  onKickOffGoal={lockedChannel ? undefined : requestConnectorLink}
                  // So is "Buy it for me" on a product card.
                  onBuyProduct={lockedChannel ? undefined : requestConnectorLink}
                  onConnectorConnected={connectHandoff.noteConnected}
                  // Edit acts on the owner's link, not the thread — but a channel-locked thread is read-only here.
                  profileLinkCardReadOnly={!!lockedChannel}
                  latestProfileLinkCard={m.profileLinkCard ? profileLinkCards.get(m.profileLinkCard.linkId) : undefined}
                  // The prompts of an ask the reader closed with the X, and could still take back —
                  // absent on every other row, so only the one that reopens something reads as
                  // clickable.
                  onReopenSurvey={
                    m.survey && m.survey.askId === reopenableSurveyAskId ? () => reopenSurvey(m.survey!.askId) : undefined
                  }
                  // Full-screen desktop chat: inset the user bubble and assistant message the same
                  // distance from the reading column's edges (no zig-zag). Narrow/docked keep no inset.
                  alignEdges={!compact && !narrow && !sideDocked}
                  // The streaming caret's live gate. `chat.streaming` — OUR OWN SSE is open — and NOT
                  // the broader `turnInFlight`: `streamOpen` is only ever set by `applyStreamEvent`,
                  // which runs from the two SSE readers alone, so a row can be receiving deltas only
                  // while that connection is up. `turnInFlight` also covers a REMOTE turn (`streaming`
                  // is false there by construction) and a reply still owed after a drop, and in both of
                  // those a row left `streamOpen` by a cut stream would take its caret back and lose
                  // its copy button. The tail check is the second lock: only the newest row can be the
                  // one being written.
                  turnActive={chat.streaming && !!m.id && m.id === tailMessageId}
                  meetingApprovalRow={m.meetingApproval ? approvals.anchored.get(m.meetingApproval.decisionId) : undefined}
                  proposalRows={m.id ? proposalRowsByMessage.get(m.id) : undefined}
                  revealStaged={!!m.id && packReveal.typing.has(m.id)}
                  revealDone={!!m.id && packReveal.revealed.has(m.id)}
                  onRevealed={packReveal.onRevealed}
                  revealProgress={packReveal.progress}
                  hideToolbar={!!m.id && !!arrivalMessage?.ids.has(m.id) && m.id !== arrivalMessage.lastId}
                  stampDate={m.id && m.id === arrivalMessage?.lastId ? arrivalMessage.createdDate : undefined}
                  // A retry IS a channel send, so it locks with the composer like a choice does.
                  onRetryFailedTurn={
                    // …and it waits out any turn in flight, ours or a remote one: a second send would race it.
                    failedTurnRetry && m.id === failedTurnRetry.rowId && !lockedChannel
                      && !chat.streaming && !chat.remoteTurnActive
                      ? () => retryFailedTurn(failedTurnRetry.text)
                      : undefined
                  }
                />
              );
              // The session's unread divider sits directly above its anchor row; the low/out-of-credits
              // card sits directly BELOW the message it was anchored to (rendered inline, like a turn).
              const isUnreadRow = !!unreadDividerId && m.id === unreadDividerId;
              const creditsCardAfter = matchesCreditsAnchor(m);
              // A row can be the first of a new DAY and the first UNREAD one at once — in fact that is
              // the common case (you leave the chat, messages arrive overnight). The two never stack:
              // the day divider absorbs the unread words and ONE hairline is drawn carrying both. Only
              // when they fall on different rows are two drawn, one each.
              const dayBefore = m.id ? dayDividers.get(m.id) : undefined;
              const kind = dividerKind(!!dayBefore, isUnreadRow);
              if (kind !== 'none' || creditsCardAfter) {
                return (
                  <div key={`row-wrap-${m.id}`} style={{ display: 'contents' }}>
                    {dayBefore && kind !== 'unread' && (
                      <DayDivider day={dayBefore} alsoUnread={kind === 'dayAndUnread'} />
                    )}
                    {kind === 'unread' && <UnreadDivider />}
                    {row}
                    {creditsCardAfter && (
                      <CreditsChatInlineCard
                        onVisibilityChange={onCreditsInlineVisibility}
                        scrollRootRef={scrollRef}
                        rootTopInset={creditsScrollTopInset}
                      />
                    )}
                  </div>
                );
              }
              return row;
            })}
            {approvals.trailing.map((row) => (
              <div key={`meeting-approval-${row.decision.id}`} style={{ boxSizing: 'border-box', width: '100%', maxWidth: 720 }}>
                <MeetingApprovalCard row={row} />
              </div>
            ))}
            {/* Fallback: IN THE ANCHOR CONVERSATION, the state is active but its anchor message isn't in
                view yet (a fresh thread, or the anchor hasn't been captured for this render) — show the
                card at the bottom. Other conversations render no inline card at all. */}
            {inCreditsAnchorConv && !creditsAnchorInView && (
              <CreditsChatInlineCard
                onVisibilityChange={onCreditsInlineVisibility}
                scrollRootRef={scrollRef}
                rootTopInset={creditsScrollTopInset}
              />
            )}
            <ChatTurnProgress
              inFlight={
                chat.streaming ||
                remoteTurnForOwner ||
                connectHandoff.pending ||
                pendingPackWalkthrough ||
                (hasStreamedRef.current && !userStopped && isWaitingForAssistant(chat.messages))
              }
              lastMessage={chat.messages[chat.messages.length - 1]}
              packPending={pendingPackWalkthrough}
              connectPending={connectHandoff.pending}
              surveyWriting={chat.surveyWriting}
              thinking={latestTurnStatus(chat.messages)}
              connectThinking={connectHandoff.handoffThinkingText}
              packThinking={t('packs.accept.loading', { maestroAgentName: briefAssistant.name })}
              aside={<SitePreviewLink conversationId={chat.conversationId} />}
            />
            {/* Transient scroll room so a just-sent message can glide to the top while its reply is in
                flight (see submitMessage / useScroll). Tagged so useScroll can measure the room it is
                actually rendering, rather than trust its own state a frame before the DOM agrees. */}
            {sendSpacer > 0 && <div aria-hidden data-send-spacer="" style={{ height: sendSpacer }} />}
          </div>
        </ScrollArea>
      )}

      {dragActive && <DragOverlay fileCount={dragFileCount} />}
      {/* Empty chat (a fresh "Talk to Maestro" thread): the agent-orb hero +
          greeting sits above the centered composer, matching native's
          ChatEmptyState and the home landing. Hidden once a message exists. */}
      {isEmptyLanding && (
        // A ScrollArea rather than a plain div, because this is the ONLY flexible child on the empty
        // landing — the transcript's ScrollArea is not rendered in this state — and `minHeight: 0`
        // lets it shrink below its content. Centred, and scrollable, so that when the hero + wrapped
        // chips outgrow a short window the overflow stays reachable rather than clipping off both ends.
        // (In the tight pane the chips are NOT here — they ride the lane above the composer — so this
        // holds only the hero.)
        <ScrollArea
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* The orb hero. Hidden only in the tiny floating panel (`compact`), which has no room for it;
              the docked pane and every wider surface show it, in the size `tight` picks (Figma
              17225:28520 tight, 17208:22003 roomy). */}
          {!compact && <EmptyLandingHero tight={chipsScrollRow} />}
          {/* The genome's suggested next steps, in the ROOMY layout: centred wrapped rows under the
              hero (Figma 17208:22003). A tap PREFILLS the composer and wakes it — the user still presses
              send, so the assistant's guess is one they can decline by editing or ignoring it. The row
              draws nothing at all when there is nothing to suggest. In the tight pane the chips move to a
              horizontal lane above the composer instead (rendered in the form below). */}
          {!chipsScrollRow && (
            <SuggestedTaskChips enabled={isEmptyLanding} layout="wrap" onPick={pickSuggestedTask} onConnected={connectHandoff.noteConnected} />
          )}
        </ScrollArea>
      )}
        </>
      )}
      {/* A live call takes over the whole panel (orb + bottom Mute/End on the VoiceCallOverlay
          surface above), so the composer is hidden while it's up — matching the native full-surface
          presentation. It returns the moment the call is minimized to the island or ends. */}
      {!voice.timing.surfaceVisible && (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // While the survey holds the slot there is no draft to send, and its free-text field is the
          // form's only input — so Enter in it would implicitly submit the form and fire an empty send.
          if (pendingSurvey) return;
          submitMessage();
        }}
        style={formStyle({ compact, narrow, sideDocked, hidden: missingHero })}
      >
        {/* The genome's suggested next steps, in the TIGHT layout: a horizontal lane pinned just above
            the composer that scrolls off the pane's edge (Figma 17225:28520). In the roomy layout the
            chips wrap under the hero instead (rendered in the empty-landing block above). */}
        {isEmptyLanding && chipsScrollRow && (
          <SuggestedTaskChips enabled={isEmptyLanding} layout="scroll" onPick={pickSuggestedTask} onConnected={connectHandoff.noteConnected} />
        )}
        {!isEmptyLanding && <ScrollToLatestButton onClick={scrollToLatest} progress={scrollProgress} />}
        {!isEmptyLanding && <HorizonLine />}
        {pendingAttachments.length > 0 && (
          <AttachmentChipRow
            attachments={pendingAttachments}
            onRemove={removeAttachment}
            onRetry={retryAttachment}
            // Figma "File Type Assets": 60px tiles in the full-size chat, 40px in the small panel
            // and on mobile web.
            size={!compact && !narrow ? 60 : 40}
          />
        )}
        {pickedContacts.length > 0 && <ContactChipRow contacts={pickedContacts} onRemove={removeContact} />}
        {/* Above the composer, and above the locked bar too — a WhatsApp/SMS thread is exactly the kind
            the user returns to cold, so the recap is if anything more useful there. */}
        {recap && recapVisible && chat.conversationId && (
          <RecapBanner recap={recap} conversationId={chat.conversationId} />
        )}
        {/* The condensed credits bar, pinned above the composer while the inline card is off screen. */}
        {showCreditsSticky && <CreditsChatStickyBar />}
        {/* WhatsApp/SMS-owned conversation: the composer is a locked bar with
            a deep link into the owning app — no input, mic, or attach. */}
        {lockedChannel ? (
          <ChannelLockedBar
            channel={lockedChannel}
            agentPlatform={lockedAgentPlatform}
            isWaGroup={lockedIsWaGroup}
            compact={compact}
          />
        ) : pendingSurvey ? (
          <SurveyWidget
            inComposer
            survey={pendingSurvey.survey}
            conversationId={chat.conversationId ?? undefined}
            messageId={pendingSurvey.message?.finalId ?? pendingSurvey.message?.id}
            surfaceVisible={surfaceVisible}
            onSubmit={(answer, text) => submitSurvey(answer, text)}
            onDismiss={() =>
              setDismissedSurveyIds((prev) => new Set(prev).add(pendingSurvey.survey.askId))
            }
          />
        ) : (
        <Composer
          compact={compact}
          narrow={narrow}
          replyPreview={pendingReply ? <PendingReplyChip reply={pendingReply} onCancel={() => setPendingReply(null)} /> : undefined}
          conversationId={chat.conversationId}
          inputText={inputText}
          onInputChange={setInputText}
          onInputAwakeChange={onInputAwakeChange}
          onSend={submitMessage}
          onComposerPaste={onComposerPaste}
          openFilePicker={openFilePicker}
          openContactPicker={() => setContactPickerOpen(true)}
          openPlacePicker={() => {
            logPageClickBi({ ...CHAT_HOME_BI, button_name: 'attach_location' });
            setPlacePickerOpen(true);
          }}
          openTimePicker={() => {
            logPageClickBi({ ...CHAT_HOME_BI, button_name: 'attach_time' });
            setTimePickerOpen(true);
          }}
          fileInputRef={fileInputRef}
          startUploads={startUploads}
          dictation={dictation}
          voiceNote={voiceNote}
          canSend={canSend}
          showStop={showStop}
          onStop={onStop}
          onStartCall={startCall}
          mention={{ open: mentions.open, agents: mentions.agents, onPick: pickMentionAgent, onDismiss: mentions.dismiss }}
        />
        )}
      </form>
      )}
      {contactPickerOpen && <ContactPicker onPick={addContact} onClose={() => setContactPickerOpen(false)} />}
      {placePickerOpen && <PlacePicker onPick={onPickPlace} onClose={() => setPlacePickerOpen(false)} />}
      {timePickerOpen && <TimePicker onPick={onPickTime} onClose={() => setTimePickerOpen(false)} />}
    </section>
  );
}


// The DAY divider: a dated hairline where the transcript crosses into a new local
// calendar day. Same shape as UnreadDivider below — the transcript's two separators
// read as one family rather than two inventions. Anchors from `computeDayDividers`.
function DayDivider({ day, alsoUnread }: { day: Date; alsoUnread?: boolean }) {
  const { t, i18n } = useTranslation();
  // The activity log's existing day words, not new chat-scoped keys: a transcript and
  // the activity list one screen over must agree on what "Yesterday" means. Both phones
  // make the same call against their own copies of those keys.
  // The ACTIVE language, not the browser's — see `dayDividerLabel`. Without it a reader whose Settings
  // pick differs from their browser locale gets a divider in two languages at once.
  const dayLabel = dayDividerLabel(
    day,
    { today: t('agents.activity.today'), yesterday: t('agents.activity.yesterday') },
    undefined,
    i18n.language || undefined,
  );
  // When this row is ALSO the first unread one, <UnreadDivider /> is not rendered separately —
  // this divider absorbs its words, so the reader gets one hairline carrying both facts
  // instead of two stacked rules saying one each.
  const label = alsoUnread
    ? combinedDividerLabel(dayLabel, t('chat.unread.newMessages'))
    : dayLabel;
  return (
    <div role="separator" aria-label={label} style={UNREAD_DIVIDER_WRAP} data-testid="chat-day-divider">
      <span aria-hidden style={UNREAD_DIVIDER_LINE} />
      <span style={UNREAD_DIVIDER_LABEL}>{label}</span>
      <span aria-hidden style={UNREAD_DIVIDER_LINE} />
    </div>
  );
}

// Quiet "New messages" hairline — anchors the first unread row of this viewing
// session (see the unread-divider block above). Deliberately whisper-weight:
// two hairlines and a small dim label, no fill, no accent.
function UnreadDivider() {
  const { t } = useTranslation();
  const label = t('chat.unread.newMessages');
  return (
    <div role="separator" aria-label={label} style={UNREAD_DIVIDER_WRAP}>
      <span aria-hidden style={UNREAD_DIVIDER_LINE} />
      <span style={UNREAD_DIVIDER_LABEL}>{label}</span>
      <span aria-hidden style={UNREAD_DIVIDER_LINE} />
    </div>
  );
}

const UNREAD_DIVIDER_WRAP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  margin: '4px 0 18px',
};

const UNREAD_DIVIDER_LINE: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: 'rgba(var(--hairline-rgb),0.22)',
};

const UNREAD_DIVIDER_LABEL: React.CSSProperties = {
  flexShrink: 0,
  fontSize: '0.6875rem',
  fontWeight: 'var(--sym-weight-semibold)',
  letterSpacing: '0.05em',
  color: 'rgba(var(--ink-rgb),0.45)',
};

// Compact "Replying to …" bar above the composer — mirrors the ReplyQuote visual language,
// with an X to cancel. Sits outside the composer pill so it never fights the input layout.
// Reply preview shown inside the composer pill (Figma node 10944:127203): a
// full-width light-grey rounded box — reply-arrow · replied text (wraps to two
// lines) · close — sitting above the textarea.
function PendingReplyChip({ reply, onCancel }: { reply: NonNullable<Message['replyTo']>; onCancel: () => void }) {
  const { t } = useTranslation();
  const text = reply.preview || t('chat.reply.replyingTo', { name: reply.sender || '' }).trim();
  return (
    <div style={PENDING_REPLY_WRAP}>
      <Reply size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden style={{ flexShrink: 0, marginTop: 1, color: 'var(--sym-ink)' }} />
      <div dir="auto" style={PENDING_REPLY_TEXT}>
        {text}
      </div>
      <button type="button" onClick={onCancel} aria-label={t('common.cancel')} style={PENDING_REPLY_CANCEL}>
        <X size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </button>
    </div>
  );
}

const PENDING_REPLY_WRAP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  marginBottom: 16,
  padding: 12,
  borderRadius: 12,
  background: 'var(--ds-surface-standard-bg)',
  width: '100%',
  boxSizing: 'border-box',
};

const PENDING_REPLY_TEXT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: '0.875rem',
  lineHeight: '1.125rem',
  color: 'var(--ds-text-secondary)',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const PENDING_REPLY_CANCEL: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  border: 'none',
  borderRadius: 7,
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--sym-ink)',
  flexShrink: 0,
};
